import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Check, X, Archive, Flag, FolderKanban, Clock, Scale, Activity } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const KINDS = {
  priorities: {
    label: "Priorities",
    icon: Flag,
    titleKey: "title",
    fields: [
      { key: "title", label: "Title", type: "input", required: true },
      { key: "detail", label: "Detail", type: "textarea" },
      { key: "rank", label: "Rank", type: "input" },
    ],
    actions: [
      { status: "completed", label: "Complete", icon: Check },
      { status: "cancelled", label: "Cancel", icon: X },
      { status: "archived", label: "Archive", icon: Archive },
    ],
  },
  projects: {
    label: "Projects",
    icon: FolderKanban,
    titleKey: "name",
    fields: [
      { key: "name", label: "Name", type: "input", required: true },
      { key: "owner", label: "Owner", type: "input" },
      { key: "detail", label: "Detail", type: "textarea" },
    ],
    actions: [
      { status: "on_hold", label: "Hold", icon: Clock },
      { status: "completed", label: "Complete", icon: Check },
      { status: "archived", label: "Archive", icon: Archive },
    ],
  },
  commitments: {
    label: "Commitments",
    icon: Clock,
    titleKey: "description",
    fields: [
      { key: "description", label: "Description", type: "input", required: true },
      { key: "owner", label: "Owner", type: "input" },
      { key: "due_date", label: "Due date", type: "date" },
    ],
    actions: [
      { status: "completed", label: "Resolve", icon: Check },
      { status: "cancelled", label: "Cancel", icon: X },
    ],
  },
  decisions: {
    label: "Decisions",
    icon: Scale,
    titleKey: "title",
    fields: [
      { key: "title", label: "Decision", type: "input", required: true },
      { key: "context", label: "Context", type: "textarea" },
      { key: "options", label: "Options", type: "textarea" },
    ],
    actions: [
      { status: "decided", label: "Mark decided", icon: Check },
      { status: "cancelled", label: "Cancel", icon: X },
    ],
  },
  risks: {
    label: "Risks",
    icon: Activity,
    titleKey: "title",
    fields: [
      { key: "title", label: "Title", type: "input", required: true },
      { key: "severity", label: "Severity", type: "input", hint: "low / medium / high" },
      { key: "detail", label: "Detail", type: "textarea" },
    ],
    actions: [
      { status: "mitigated", label: "Mitigated", icon: Check },
      { status: "archived", label: "Archive", icon: Archive },
    ],
  },
};

const TAB_ORDER = ["priorities", "projects", "commitments", "decisions", "risks"];

function statusColor(status) {
  if (["completed", "decided", "mitigated"].includes(status)) return "text-emerald-400";
  if (["cancelled"].includes(status)) return "text-wr-muted";
  if (["on_hold"].includes(status)) return "text-amber-400";
  return "text-wr-red";
}

function CreateDialog({ kind, onCreated }) {
  const cfg = KINDS[kind];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== undefined && v !== "")
      );
      if (payload.due_date) payload.due_date = new Date(payload.due_date).toISOString();
      await api.post(`/board/${kind}`, payload);
      toast.success(`${cfg.label.slice(0, -1)} created.`);
      setForm({});
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`create-${kind}`}
          className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider text-xs h-10"
        >
          <Plus size={15} className="mr-2" /> New
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-wr-surface border-wr-border rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="wr-heading text-white">New {cfg.label.slice(0, -1)}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid={`form-${kind}`}>
          {cfg.fields.map((f) => (
            <div key={f.key}>
              <label className="wr-label">{f.label}{f.required && <span className="text-wr-red"> *</span>}</label>
              {f.hint && <p className="text-[0.68rem] text-wr-muted mt-0.5">{f.hint}</p>}
              {f.type === "textarea" ? (
                <Textarea
                  data-testid={`field-${kind}-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0"
                />
              ) : (
                <Input
                  type={f.type === "date" ? "date" : "text"}
                  required={f.required}
                  data-testid={`field-${kind}-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0"
                />
              )}
            </div>
          ))}
          <Button
            type="submit"
            disabled={busy}
            data-testid={`submit-${kind}`}
            className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider"
          >
            {busy ? "Saving…" : "Create"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CommandBoard() {
  const [params, setParams] = useSearchParams();
  const tab = TAB_ORDER.includes(params.get("tab")) ? params.get("tab") : "priorities";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const cfg = KINDS[tab];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/board/${tab}`);
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/board/${tab}/${id}`, { status });
      toast.success("Updated.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
      <p className="wr-label !text-wr-red">▸ SARGE · COMMAND BOARD</p>
      <h1 className="wr-heading font-bold text-white text-2xl sm:text-3xl mt-2">Operating record.</h1>

      {/* Tabs */}
      <div className="mt-6 flex gap-1 border-b border-wr-border overflow-x-auto" data-testid="board-tabs">
        {TAB_ORDER.map((k) => {
          const Icon = KINDS[k].icon;
          const active = tab === k;
          return (
            <button
              key={k}
              onClick={() => setParams({ tab: k })}
              data-testid={`tab-${k}`}
              className={`flex items-center gap-2 px-4 py-2.5 wr-mono text-xs uppercase tracking-wider whitespace-nowrap border-b-2 -mb-px transition-colors ${
                active ? "border-wr-red text-white" : "border-transparent text-wr-sub hover:text-white"
              }`}
            >
              <Icon size={14} /> {KINDS[k].label}
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end">
        <CreateDialog kind={tab} onCreated={load} />
      </div>

      <div className="mt-4">
        {loading ? (
          <p className="wr-label" data-testid="board-loading">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="border border-wr-border bg-wr-surface p-8 text-center" data-testid="board-empty">
            <cfg.icon className="mx-auto text-wr-red mb-3" size={24} />
            <p className="text-sm text-wr-sub">No {cfg.label.toLowerCase()} yet.</p>
          </div>
        ) : (
          <div className="border-t border-l border-wr-border grid grid-cols-1 md:grid-cols-2">
            {rows.map((r) => (
              <div
                key={r.id}
                data-testid={`row-${tab}-${r.id}`}
                className="border-r border-b border-wr-border p-5 bg-wr-surface"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="wr-heading font-semibold text-white">{r[cfg.titleKey]}</p>
                  <span className={`wr-mono text-[0.68rem] uppercase shrink-0 ${statusColor(r.status)}`}>
                    {r.status}
                  </span>
                </div>
                {r.detail && <p className="text-sm text-wr-sub mt-2">{r.detail}</p>}
                {r.context && <p className="text-sm text-wr-sub mt-2">{r.context}</p>}
                {r.options && <p className="text-sm text-wr-muted mt-2 whitespace-pre-wrap">{r.options}</p>}
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  {r.owner && <span className="wr-mono text-xs text-wr-muted">owner: {r.owner}</span>}
                  {r.due_date && (
                    <span className="wr-mono text-xs text-wr-muted">
                      due: {new Date(r.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {r.severity && <span className="wr-mono text-xs text-wr-muted">severity: {r.severity}</span>}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {cfg.actions.map((a) => {
                    const AIcon = a.icon;
                    const done = r.status === a.status;
                    return (
                      <button
                        key={a.status}
                        onClick={() => setStatus(r.id, a.status)}
                        disabled={done}
                        data-testid={`action-${tab}-${r.id}-${a.status}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-wr-border wr-mono text-[0.68rem] uppercase tracking-wider text-wr-sub hover:text-white hover:border-wr-borderFocus disabled:opacity-40 transition-colors"
                      >
                        <AIcon size={12} /> {a.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
